import { Button, IconButton, Stack } from "@mui/material";
import { useState } from "react";
import { DrawerLayout } from "@/components/common/drawerLayout";
import { ArrowLeftIcon } from "@/components/common/icons";
import AddToWaitlistButton from "@/components/waitlist/dialog/addToWaitlistButton";
import AddToWaitlistDialog from "@/components/waitlist/dialog/addToWaitlistDialog";
import { VIOLET } from "@/config/mui/colorPalette";
import useFeatureFlags from "@/hooks/common/useFeatureFlags";
import useFeatureManagement from "@/hooks/common/useFeatureManagement";
import { useAddToWaitlistDialog } from "@/hooks/waitlist/useAddToWaitlistDialog";
import { DrawerStep } from "./multiProviderAppointmentDrawer";
import {
  AppointmentServiceInput,
  useMultiProviderAppointmentDrawerContext,
} from "./multiProviderAppointmentDrawerContext";
import { TimeSelectionContent } from "./timeSelectionContent";

type Props = {
  onSetDrawerStep: (step: DrawerStep) => void;
  onConfirmDate: () => void;
};

/**
 * Creates local copy of date and appointmentServices, so that the original
 * date and appointmentServices in context are not modified until user confirms
 * the time selection.
 */
export const MultiProviderAppointmentDrawerTimeSelectionForm = ({
  onSetDrawerStep,
  onConfirmDate,
}: Props) => {
  const { waitlistV1Enabled } = useFeatureFlags();
  const { waitlistEnabled } = useFeatureManagement();

  const {
    appointmentId,
    startDate,
    appointmentServices,
    appointmentServicesProviders,
    client,
    availableServices,
    isRebooking,
    setAppointmentServices,
  } = useMultiProviderAppointmentDrawerContext();

  const [timeSelectionDate, setTimeSelectionDate] = useState<Date>(startDate);

  const waitlist = useAddToWaitlistDialog({
    clientId: client?.id,
    appointmentDate: timeSelectionDate,
  });
  const [
    timeSelectionAppointmentServices,
    setTimeSelectionAppointmentServices,
  ] = useState<AppointmentServiceInput[]>(() =>
    appointmentServices.map((appointmentService) => ({ ...appointmentService }))
  );

  const handleSetTimeSelectionAppointmentServices = (
    appointmentServices: AppointmentServiceInput[]
  ) => {
    setTimeSelectionAppointmentServices(appointmentServices);
    if (
      appointmentServices.length > 0 &&
      appointmentServices[0].startTime !== timeSelectionDate
    ) {
      setTimeSelectionDate(appointmentServices[0].startTime);
    }
  };

  const handleSaveTimeSelector = () => {
    setAppointmentServices(timeSelectionAppointmentServices);
    onConfirmDate();
    onSetDrawerStep(DrawerStep.DEFAULT);
  };

  return (
    <>
      <DrawerLayout
        headerProps={{
          title: "Select date & time",
          leadingAction: (
            <IconButton onClick={() => onSetDrawerStep(DrawerStep.DEFAULT)}>
              <ArrowLeftIcon size={24} color={VIOLET[90]} />
            </IconButton>
          ),
        }}
        content={
          <TimeSelectionContent
            appointmentId={isRebooking ? undefined : appointmentId}
            date={timeSelectionDate}
            appointmentServices={timeSelectionAppointmentServices}
            onAppointmentServicesChange={
              handleSetTimeSelectionAppointmentServices
            }
            providers={appointmentServicesProviders}
            availableServices={availableServices}
            client={client}
            isRebooking={isRebooking}
          />
        }
        actions={
          <Stack spacing={2} width="100%">
            {waitlistV1Enabled && waitlistEnabled && (
              <AddToWaitlistButton waitlist={waitlist} client={client} />
            )}
            <Stack direction="row" spacing={2}>
              <Button
                onClick={() => onSetDrawerStep(DrawerStep.DEFAULT)}
                variant="outlined"
                sx={{ flex: 1 }}
                size="small"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSaveTimeSelector}
                variant="contained"
                sx={{ flex: 1 }}
                size="small"
              >
                Save
              </Button>
            </Stack>
          </Stack>
        }
      />
      <AddToWaitlistDialog
        key={`${client?.id}-${timeSelectionDate?.getTime()}`}
        waitlist={waitlist}
        currentAppointmentDate={timeSelectionDate}
      />
    </>
  );
};
